import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssetType,
  FileRoleEnum,
  IAddresses,
  IAllRiskItem,
  IBank,
  IBankBranches,
  IBrokerage,
  IBuilding,
  ICaravan,
  IClaimCategories,
  IContent,
  IDeleteAssets,
  IInsurersDetails,
  IMotorcycle,
  IPostalCodeAreas,
  IPremiaLOV,
  IProgressChecker,
  IQuoteRequest,
  ISchedule,
  ISpecialConditionAssets,
  ISpecifiedItem,
  IUser,
  IUserResetPassword,
  IVehicle,
  TAsset,
} from '@takectrl-multiapp/svcs-share-config';
import { INotes, ISendNotePayload, ISendNoteResponse, IUserQuoteFeedbackResponse } from 'libs/svcs-share-config/src/lib/interfaces/portal.interface';
import { Observable } from 'rxjs';
import { ICtrlResponse, IPortalTableResponse } from '../interfaces/api.interface';
import {
  IDownloadScheduleResponse,
  IReRunQuotesResponse,
  IRevertQuoteResponse,
} from './../../../../svcs-core-business-portal/src/lib/interfaces/quotes';
import { IServerStatusResponse } from './../../../../svcs-core-business-portal/src/lib/interfaces/server';
import { IAdminBrokers, IUserRenewals } from './../../../../svcs-core-business-portal/src/lib/interfaces/user';
import { HTTPRequestType } from './../../../../svcs-core/src/lib/types/http-requests.type';
import { APIHttpService } from './api-http.service';

@Injectable({
  providedIn: 'root',
})
export class APIPortalService {
  constructor(private readonly httpService: APIHttpService) {}

  getPortalLoggedInUser(): Observable<ICtrlResponse<IUser>> {
    return this.httpService.get(`/users/`);
  }

  validateAccessToken(): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/token/test/`);
  }

  getTableData(endpoint: string): Observable<ICtrlResponse<IPortalTableResponse>> {
    return this.httpService.get(endpoint);
  }

  getServerStatus(): Observable<ICtrlResponse<IServerStatusResponse>> {
    return this.httpService.get(`/health/details/`);
  }

  getServerCacheTypes(): Observable<ICtrlResponse<IServerStatusResponse>> {
    return this.httpService.get(`/admin/cache/cache_types/`);
  }

  clearServerCache(selectedCacheType: String): Observable<ICtrlResponse<any>> {
    return this.httpService.delete(`/admin/cache/${selectedCacheType}/`);
  }

  getUserNotes(userId: number): Observable<ICtrlResponse<Array<INotes>>> {
    return this.httpService.get('/users/notes/' + userId + '/');
  }

  sendNote(userId: number, payload: ISendNotePayload): Observable<ICtrlResponse<ISendNoteResponse>> {
    return this.httpService.post('/users/notes/' + userId + '/', payload);
  }

  getSpecificUser(userId: number): Observable<ICtrlResponse<IUser>> {
    return this.httpService.get(`/admin/users/${userId}/`);
  }

  getUWQ(section: string): Observable<ICtrlResponse<unknown>> {
    return this.httpService.get(`/progress/questions/${section}/`);
  }

  getUserAssets(userId: number): Observable<ICtrlResponse<unknown>> {
    return this.httpService.get(`/admin/assets/${userId}/?only_return_latest=true`);
  }

  getDocuments(userId: number): Observable<any> {
    return this.httpService.get(`/files/list/${userId}/advisor/`);
  }

  getFileUploadRules(): Observable<any> {
    return this.httpService.get(`/files/rules/`);
  }

  uploadDocument(userId: number, role: FileRoleEnum = FileRoleEnum.advisor, formData: FormData): Observable<ICtrlResponse<unknown>> {
    return this.httpService.post(`/files/${userId}/${role}/`, formData);
  }

  getRenewals(userId: number): Observable<ICtrlResponse<Array<IUserRenewals>>> {
    return this.httpService.get(`/admin/renewals/${userId}/`);
  }

  resetPassword(userEmail: string): Observable<ICtrlResponse<IUserResetPassword>> {
    return this.httpService.post(`/users/update/password/`, { email: userEmail });
  }

  getPolicies(userId: number): Observable<ICtrlResponse<Array<ISchedule>>> {
    return this.httpService.get(`/admin/users/${userId}/schedules/`);
  }

  cancelPolicy(userId: number, scheduleId: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/schedules/cancel/${userId}/${scheduleId}/`, payload);
  }

  fetchPolicyOrQuote(payload: any): Observable<any> {
    return this.httpService.post(`/update/quote/`, payload);
  }

  fetchRenewal(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/renewal/difference/`, payload);
  }

  fetchMiwayPolicy(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/policies/sync/miway`, payload);
  }

  getUserQuotes(userId: number, params?: string): Observable<ICtrlResponse<IPortalTableResponse>> {
    return this.httpService.get(`/admin/users/${userId}/quote-requests/?get_related=true${params ? params : ''}`);
  }

  getSpecialConditions(): Observable<ICtrlResponse<ISpecialConditionAssets>> {
    return this.httpService.get(`/special-conditions/`);
  }

  getInsurers(): Observable<ICtrlResponse<Array<IInsurersDetails>>> {
    return this.httpService.get(`/admin/insurers/`);
  }

  rerunQuotes(userId: number, quoteRequestId: number): Observable<ICtrlResponse<IReRunQuotesResponse>> {
    return this.httpService.post(`/run/quotes/${userId}/${quoteRequestId}/`, {});
  }

  revertQuotes(userId: number): Observable<ICtrlResponse<IRevertQuoteResponse>> {
    return this.httpService.post(`/revert-to-previous-quote/${userId}/`, {});
  }

  downloadSchedule(payload: any): Observable<ICtrlResponse<IDownloadScheduleResponse>> {
    return this.httpService.post(`/admin/reprint/quote/`, payload);
  }

  getBanks(): Observable<ICtrlResponse<Array<IBank>>> {
    return this.httpService.get(`/banks/`);
  }

  getBankBranches(bank: string): Observable<ICtrlResponse<IBankBranches>> {
    return this.httpService.get(`/bank-branch-codes/${bank}`);
  }

  getPremiaBanks(): Observable<ICtrlResponse<Array<IPremiaLOV>>> {
    return this.httpService.get(`/providers/premia/values/banks/`);
  }

  getPremiaBankBranches(bank: string): Observable<ICtrlResponse<Array<IPremiaLOV>>> {
    return this.httpService.get(`/providers/premia/values/banks_branches/?bank_code=${bank}`);
  }

  getPremiaAccountTypes(): Observable<ICtrlResponse<Array<IPremiaLOV>>> {
    return this.httpService.get(`/providers/premia/values/banks_account_types/`);
  }

  getPremiaRelationshipTypes(): Observable<ICtrlResponse<Array<IPremiaLOV>>> {
    return this.httpService.get(`/providers/premia/values/banks_relationship_types/`);
  }

  getCasa(): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/casa/combined`);
  }

  adminConvertKingPrice(user_id: number, quote_request_id: number, quote_nr: string): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/quotes/activate/king_price/${user_id}/${quote_request_id}/${quote_nr}/`, {});
  }

  adminConvertMiway(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/quotes/converts/miway/`, payload);
  }

  adminConvertPremia(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/broker/providers/premia/clients/banking_details/`, payload);
  }

  adminConvertBrolinkQuotes(user_id: number, quote_request_id: number, quote_nr: string, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/quotes/put/convert/brolink/${user_id}/${quote_request_id}/${quote_nr}/`, payload);
  }

  updateQuoteRequest(quote_request_id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/quote-requests/${quote_request_id}/`, payload);
  }

  createDBKPPolicy(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/quotes/converts/king-price/`, payload);
  }

  getErrorsFromQuote(quote_request_id: number, insurer_id?: string): Observable<ICtrlResponse<IUserQuoteFeedbackResponse>> {
    return this.httpService.get(`/admin/quote-requests/${quote_request_id}/errors/${insurer_id ? insurer_id + '/' : ''}`);
  }

  fetchUserEndorsement(user_id: number, endorsement_request_id: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/endorsements-admin-update/${user_id}/${endorsement_request_id}/`, {});
  }

  patchUsers(id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/users/${id}/`, payload);
  }

  getPeerlessCoc(params: any): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/reprint/coc/`, params);
  }

  getInsurerBasedOnId(insurer_id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/insurer/${insurer_id}/`);
  }

  getPartnerBasedOnId(partner_id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/partners/${partner_id}/`);
  }

  getAddressBasedOnId(address_id: number): Observable<ICtrlResponse<IAddresses>> {
    return this.httpService.get(`/admin/address/${address_id}/`);
  }

  getBrokerageBasedOnId(brokerage_id: number): Observable<ICtrlResponse<IBrokerage>> {
    return this.httpService.get(`/admin/brokerage/?id=${brokerage_id}`);
  }

  getEmailAddressBasedOnId(email_address_id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/email_addresses/${email_address_id}/`);
  }

  setInsurer(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/insurers/${payload.id}/`, payload);
  }

  updatePartner(partner_id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/partners/${partner_id}/`, payload);
  }

  updateBrokerage(brokerage_id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/brokerages/${brokerage_id}/`, payload);
  }

  updateAddress(address_id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/addresses/${address_id}/`, payload);
  }

  updateEmailAddress(id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/email_addresses/${id}/`, payload);
  }

  checkWhatInputFieldsAreEditable(endpoint: string, httpRequest?: HTTPRequestType): Observable<ICtrlResponse<any>> {
    const headers = new HttpHeaders({
      'x-ctrl-endpoint-permissions': 'true',
    });
    if (httpRequest && httpRequest === 'post') {
      return this.httpService.post(endpoint, null, headers);
    } else {
      return this.httpService.patch(endpoint, null, null, headers);
    }
  }

  getEndpointBasedOnId(endpoint: string, id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`${endpoint}${id}/`);
  }

  patchToEndpoint(endpoint: string, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(endpoint, payload);
  }

  sendMail(mail_template_name: string, body?: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/mails/${mail_template_name}/`, body ? body : null);
  }

  postPermission(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/permissions/`, payload);
  }

  postAddress(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/addresses/`, payload);
  }

  postBrokerage(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/brokerages/`, payload);
  }

  postEmailAddress(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/email_addresses/`, payload);
  }

  getIntercomMessageBasedOnId(id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/intercom_messages/`, { id: id });
  }

  updateIntercomMessages(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/intercom_messages/${payload.id}/`, payload);
  }

  getWhatsappMessageBasedOnId(id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/b2b-whatsapp-messages/${id}/`);
  }

  updateWhatsAppMessages(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/b2b-whatsapp-messages/${payload.id}/`, payload);
  }

  getBrokerBasedOnId(id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/broker/${id}/`);
  }

  getRoleBasedOnId(id: number): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/roles/${id}/`);
  }

  updateBroker(id: number, payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.patch(`/admin/brokers/${id}/`, payload);
  }

  postBroker(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/brokers/`, payload);
  }

  getLisOfBrokers(): Observable<ICtrlResponse<Array<IAdminBrokers>>> {
    return this.httpService.get(`/admin/brokers/`);
  }

  addNewPartner(payload: any): Observable<ICtrlResponse<Array<any>>> {
    return this.httpService.post(`/admin/partners/`, payload);
  }

  getAllUsers(params?: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/users/${params}`);
  }

  addSharedBroker(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/shared_brokers/`, payload);
  }

  searchEndPointForDropdown(endpoint: string, params?: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`${endpoint}?${params ? params : ''}`);
  }

  getAllBrokerages(params?: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/brokerages/${params ? params : ''}`);
  }

  getEmailAddresses(): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/email_addresses/`);
  }

  addBrokerEmail(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/brokers/email/`, payload);
  }

  getAllRoles(): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/admin/permissions/`);
  }

  addTrackingDevice(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/tracking-devices/`, payload);
  }

  getAnyEndpointWithSearch(endpoint_with_search: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(endpoint_with_search);
  }

  createNewQuote(requestPayload: any): Observable<ICtrlResponse<IQuoteRequest>> {
    return this.httpService.post(`/admin/quote-requests/`, requestPayload);
  }

  createAssetSpecifiedItems(payload: ISpecifiedItem): Observable<ICtrlResponse<ISpecifiedItem>> {
    return this.httpService.post(`/admin/specified_items/`, payload);
  }

  createAssetVehicles(payload: IVehicle): Observable<ICtrlResponse<IVehicle>> {
    return this.httpService.post(`/admin/vehicles/`, payload);
  }

  createAssetMotorCycles(payload: IMotorcycle): Observable<ICtrlResponse<IMotorcycle>> {
    return this.httpService.post(`/admin/motorcycles/`, payload);
  }

  createAssetCaravans(payload: ICaravan): Observable<ICtrlResponse<ICaravan>> {
    return this.httpService.post(`/admin/caravans/`, payload);
  }

  createAssetBuildings(payload: IBuilding): Observable<ICtrlResponse<IBuilding>> {
    return this.httpService.post(`/admin/buildings/`, payload);
  }

  createAssetContents(payload: IContent): Observable<ICtrlResponse<IContent>> {
    return this.httpService.post(`/admin/contents/`, payload);
  }

  createAssetAllRisks(payload: IAllRiskItem): Observable<ICtrlResponse<IMotorcycle>> {
    return this.httpService.post(`/admin/all-risks/`, payload);
  }

  removeAssetFromQuoteRequest(asset_type: AssetType, id: number): Observable<ICtrlResponse<TAsset>> {
    return this.httpService.patch(`/admin/${asset_type}/${id}/`, { quote_request_id: null });
  }

  progressChecker(asset_id: number, asset_type: string): Observable<ICtrlResponse<IProgressChecker>> {
    return this.httpService.get(`/admin/progress/check/${asset_type}/${asset_id}/`);
  }

  getQuoteRequest(user_id: number, quote_request_id: number): Observable<ICtrlResponse<IQuoteRequest>> {
    return this.httpService.get(`/admin/users/${user_id}/quote-requests/${quote_request_id}/`);
  }

  getAddressFromString(addressResult: string): Observable<ICtrlResponse<IPostalCodeAreas>> {
    return this.httpService.post(`/address/fulltext`, { fulltext: addressResult }, null);
  }

  renewalPull(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/renewal/renewals_pull/`, payload);
  }

  renewalRun(payload: any): Observable<ICtrlResponse<any>> {
    return this.httpService.post(`/admin/renewal/renewals_run/`, payload);
  }

  createNewUser(payload: any): Observable<ICtrlResponse<IUser>> {
    return this.httpService.post(`/users/`, payload);
  }

  getCarMake(): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/motorvehicles/makes/`);
  }

  getMotorcycleMake(): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/motorcycles/makes/`);
  }

  getCarModel(make: string, year: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/motorvehicles/makes/${encodeURIComponent(make)}/models/${year}`);
  }

  getMotorcycleModel(make: string, year: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/motorcycles/makes/${encodeURIComponent(make)}/models/${year}`);
  }

  getCarModelVariant(make: string, year: string, model: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/motorvehicles/model/details/?year=${year}&make=${make}&model=${model}`);
  }

  getMotorcycleModelVariant(make: string, year: string, model: string): Observable<ICtrlResponse<any>> {
    return this.httpService.get(`/motorcycles/model/details/?year=${year}&make=${make}&model=${model}`);
  }

  getClaimCategories(): Observable<ICtrlResponse<Array<IClaimCategories>>> {
    return this.httpService.get(`/claim-categories/`);
  }

  removeAsset(deleteAsset: IDeleteAssets, quoteRequestId: number): Observable<ICtrlResponse<any>> {
    return this.httpService.post<ICtrlResponse<any>>(`/quote-requests/${quoteRequestId}/remove/assets/`, deleteAsset);
  }
}
